import {
  Component,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  ViewChild,
  inject,
  Input,
  effect,
} from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { ThemeService } from '../services/theme';
import { PriceScalePipe } from '../pipes/price-scale.pipe';
import { ForexService } from '../services/forex';

Chart.register(...registerables);

export interface ConsensusHistoryEntry {
  date: string;
  price: number | null;
  currency?: string;
}

@Component({
  selector: 'app-price-line-chart',
  imports: [],
  providers: [PriceScalePipe],
  standalone: true,
  templateUrl: './price-line-chart.html',
  styleUrl: './price-line-chart.scss',
})
export class PriceLineChart implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartCanvas')
  canvasRef!: ElementRef<HTMLCanvasElement>;
  private chart?: Chart;
  private readonly themeService = inject(ThemeService);
  private readonly priceScalePipe = inject(PriceScalePipe);
  private readonly forexService = inject(ForexService);
  private readonly eurFormatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  constructor() {
    effect(() => {
      if (this.forexService.loaded() && this.canvasRef) {
        this.redraw();
      }
    });
  }

  @Input() highTarget: number | null = null;
  @Input() avgTarget: number | null = null;
  @Input() lowTarget: number | null = null;
  @Input() currency?: string;
  @Input() forceCurrency?: string;
  @Input() consensusHistory: ConsensusHistoryEntry[] | null = null;
  @Input() currentPrice: number | null = null;
  // Canvas height in px — defaults to V2's own hardcoded 113px (see price-line-chart.scss).
  @Input() height = 113;
  // Historical-price line + its end point — defaults to V2's hardcoded blue, so only a caller
  // that explicitly wants a theme-driven color (e.g. V3's --v3-primary/--v3-finnet-primary)
  // needs to pass one. Must be an already-resolved color (rgb/hex) — canvas can't read CSS vars.
  @Input() primaryColor = '#248EFF';
  // Text next to each target dot (high/avg/low price) — undefined preserves V2's light/dark
  // isLight() branch untouched.
  @Input() targetLabelColor?: string;
  // When true, every horizontal grid line uses the (darker, fully-opaque) bottom-line color
  // instead of just the last one — V2's own fading-toward-the-top look is the default.
  @Input() uniformGridColor = false;
  // The high/low target dot + connector line + label text (not the historical-price line —
  // see primaryColor above, or the neutral/average-target grey, which stays V2's own literal
  // regardless). Undefined preserves V2's hardcoded green/red.
  @Input() targetPositiveColor?: string;
  @Input() targetNegativeColor?: string;
  // The gradient fill under the historical-price line. Defaults to V2's look; V3 opts out
  // entirely (all themes, all sizes).
  @Input() showAreaFill = true;
  // Left-to-right reveal on mount (V3 callers only). Inherently mount-only: redraw() rebuilds
  // the Chart.js instance but never the canvas node, so the CSS animation never retriggers.
  @Input() animateIn = false;
  // V3 Zero only — shrinks tooltip/tick/target-label text to 10px.
  @Input() compactFonts = false;
  @Input() labelFont = 'Lato';

  private readonly GERMAN_MONTHS = [
    'Jan',
    'Feb',
    'Mär',
    'Apr',
    'Mai',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Okt',
    'Nov',
    'Dez',
  ];

  private get filteredHistory(): ConsensusHistoryEntry[] {
    return (this.consensusHistory ?? [])
      .filter((e) => {
        if (!e) return false;
        const d = new Date(e.date);
        return !isNaN(d.getTime());
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  private computeMonthTicks(history: ConsensusHistoryEntry[]): { index: number; label: string }[] {
    const seen = new Set<string>();
    const ticks: { index: number; label: string }[] = [];

    // history is sorted ascending by date (see filteredHistory getter)
    for (let i = 0; i < history.length; i++) {
      const d = new Date(history[i].date);
      const year = d.getUTCFullYear();
      const month = d.getUTCMonth();
      const key = `${year}-${month}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      const midMs = Date.UTC(year, month, Math.ceil(daysInMonth / 2));

      // Among all data points in this month, pick the one closest to the calendar mid-point.
      // Constraining to the same month means sparse or partial months (e.g. only the last
      // week of March, or the first two weeks of June) stay within their own month's data.
      let bestIndex = i;
      let bestDiff = Math.abs(new Date(history[i].date).getTime() - midMs);

      for (let j = i + 1; j < history.length; j++) {
        const jDate = new Date(history[j].date);
        if (jDate.getUTCMonth() !== month || jDate.getUTCFullYear() !== year) break;
        const diff = Math.abs(jDate.getTime() - midMs);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIndex = j;
        }
      }

      ticks.push({ index: bestIndex, label: this.GERMAN_MONTHS[month] });
    }

    return ticks;
  }

  ngAfterViewInit() {
    this.createChart();
  }

  ngOnChanges() {
    if (this.canvasRef) {
      this.redraw();
    }
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }

  private redraw() {
    this.chart?.destroy();
    this.chart = undefined;
    this.createChart();
  }

  // Fill-gradient stops need separate r/g/b channels (to vary only alpha) — primaryColor
  // arrives as an already-resolved hex or rgb(a) string, never a CSS var. Falls back to the
  // original hardcoded blue for anything else unparseable.
  private parseRgb(color: string): [number, number, number] {
    const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
      return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
    }
    const hexMatch = color.match(/^#([0-9a-f]{6})$/i);
    if (hexMatch) {
      const hex = hexMatch[1];
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }
    return [0, 118, 245];
  }

  private createChart() {
    const ctx = this.canvasRef.nativeElement.getContext('2d');
    if (!ctx) return;

    const fxReady = !!(this.currency && this.forexService.loaded());
    const toDisplayTarget = (v: number | null): number | null => {
      if (v === null) return null;
      if (!fxReady) return v;
      return this.forexService.toEuro(v, this.currency!) ?? v;
    };
    const formatDisplayValue = (v: number): string =>
      fxReady
        ? this.eurFormatter.format(v) + ' €'
        : this.priceScalePipe.transform(v, 2, false, this.currency);

    const highDisplay = toDisplayTarget(this.highTarget);
    const avgDisplay = toDisplayTarget(this.avgTarget);
    const lowDisplay = toDisplayTarget(this.lowTarget);

    const forexLoaded = this.forexService.loaded();
    const filtered = this.filteredHistory;
    if (filtered.length === 0) return;
    const hasRealPriceTargets = filtered.some(
      (e) => typeof e.price === 'number' && Number.isFinite(e.price),
    );
    const fxAvgTarget = avgDisplay ?? this.avgTarget;
    const needsFallback = filtered.some(
      (e) => typeof e.price !== 'number' || !Number.isFinite(e.price),
    );
    if (fxAvgTarget === null && needsFallback) return;

    const data = filtered.map((e) => {
      const raw = e.price;
      if (typeof raw !== 'number' || !Number.isFinite(raw)) return fxAvgTarget!;
      const entryCurrency = this.forceCurrency ?? e.currency ?? this.currency;
      if (entryCurrency && forexLoaded) {
        return this.forexService.toEuro(raw, entryCurrency) ?? raw;
      }
      return raw;
    });
    const monthTicks = this.computeMonthTicks(filtered);
    const monthTickMap = new Map<number, string>(monthTicks.map((t) => [t.index, t.label]));

    // Derived from the [height] input, not canvas.height — that reflects whatever Chart.js set
    // it to on a previous render, so it can desync from a caller that changes height at runtime.
    let backgroundColor: string | CanvasGradient = 'transparent';
    if (this.showAreaFill) {
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
      const chartHeight = this.height * dpr;
      const [r, g, b] = this.parseRgb(this.primaryColor);
      const gradient = ctx.createLinearGradient(0, 0, 0, chartHeight);
      gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.2)`);
      gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.02)`);
      gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
      backgroundColor = gradient;
    }

    const isLight = this.themeService.isLight();
    const gridColor = isLight ? 'rgba(217, 221, 224, 0.3)' : 'rgba(58, 60, 61, 0.5)';
    const bottomLineColor = isLight ? 'rgba(217, 221, 224, 1)' : 'rgba(58, 60, 61, 1)';
    const labelColor = isLight ? '#777F89' : '#C5CBD1';

    const allValues = [...data];
    if (highDisplay !== null) allValues.push(highDisplay);
    if (avgDisplay !== null) allValues.push(avgDisplay);
    if (lowDisplay !== null) allValues.push(lowDisplay);
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = max - min || min * 0.1 || 1;
    const step = range / 3;

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map((_, i) => String(i)),
        datasets: [
          {
            data,
            borderColor: this.primaryColor,
            borderWidth: 1.5,
            borderDash: hasRealPriceTargets ? [] : [4, 4],
            backgroundColor,
            fill: this.showAreaFill,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: this.primaryColor,
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            displayColors: false,
            backgroundColor: isLight ? '#ffffff' : '#1e2328',
            borderColor: isLight ? 'rgba(217, 221, 224, 1)' : 'rgba(58, 60, 61, 1)',
            borderWidth: 1,
            titleColor: labelColor,
            bodyColor: isLight ? '#000000' : '#D9DDE0',
            titleFont: { size: this.compactFonts ? 10 : 11 },
            bodyFont: { size: this.compactFonts ? 10 : 12, weight: 600 },
            padding: 8,
            callbacks: {
              title: (items) => {
                if (items.length === 0) return '';
                const entry = filtered[items[0].dataIndex];
                if (!entry) return '';
                const d = new Date(entry.date);
                if (isNaN(d.getTime())) return '';
                return `${d.getUTCDate()}. ${this.GERMAN_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
              },
              label: (item) => formatDisplayValue(item.raw as number),
            },
          },
        },
        scales: {
          x: {
            display: true,
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: labelColor,
              font: { size: this.compactFonts ? 10 : 12 },
              maxRotation: 0,
              autoSkip: false,
              callback: (_value, index) => monthTickMap.get(index) ?? '',
            },
          },
          y: {
            display: true,
            min: min - step * 0.5,
            max: max + step * 0.5,
            afterBuildTicks: (axis) => {
              // Force exactly 4 ticks (3 inner + 1 bottom)
              const bottom = min - step * 0.5;
              const top = max + step * 0.5;
              const tickStep = (top - bottom) / 3;
              axis.ticks = [
                { value: top },
                { value: bottom + tickStep * 2 },
                { value: bottom + tickStep },
                { value: bottom },
              ];
            },
            grid: {
              display: false,
            },
            border: { display: false },
            ticks: { display: false },
          },
        },
        layout: {
          padding: { left: 0, right: 150, top: 4, bottom: 0 },
        },
      },
      plugins: [
        {
          id: 'fullWidthGrid',
          beforeDraw: (chart) => {
            const c = chart.ctx;
            const yScale = chart.scales['y'];
            const canvasWidth = chart.width;
            const ticks = yScale.ticks;
            ticks.forEach((tick, i) => {
              const y = yScale.getPixelForValue(tick.value);
              const isBottom = i === ticks.length - 1;
              c.save();
              c.beginPath();
              c.moveTo(0, y);
              c.lineTo(canvasWidth, y);
              c.strokeStyle = isBottom || this.uniformGridColor ? bottomLineColor : gridColor;
              c.lineWidth = this.compactFonts ? 0.5 : 1;
              c.stroke();
              c.restore();
            });
          },
        },
        {
          id: 'dashedConnectors',
          afterDraw: (chart) => {
            const c = chart.ctx;
            const yScale = chart.scales['y'];
            const meta = chart.getDatasetMeta(0);
            const lastPoint = meta.data[meta.data.length - 1];
            if (!lastPoint) return;
            const rightX = chart.chartArea.right + 80;
            const GREEN = this.targetPositiveColor ?? (isLight ? '#34B287' : '#37BE90');
            const GREY = isLight ? '#777F89' : '#C5CBD1';
            const RED = this.targetNegativeColor ?? (isLight ? '#FA455A' : '#FC8593');
            const allTargets = [
              { value: highDisplay, hex: GREEN },
              { value: avgDisplay, hex: GREY },
              { value: lowDisplay, hex: RED },
            ];
            const nonNull = allTargets.filter((t) => t.value !== null);
            const uniqueFormatted = new Set(nonNull.map((t) => formatDisplayValue(t.value!)));
            let targets: typeof allTargets;
            if (uniqueFormatted.size === 1 && nonNull.length > 1) {
              const singleValue = nonNull[0].value!;
              const hex =
                this.currentPrice !== null && singleValue > this.currentPrice
                  ? GREEN
                  : this.currentPrice !== null && singleValue < this.currentPrice
                    ? RED
                    : GREY;
              targets = [{ value: singleValue, hex }];
            } else {
              targets = allTargets;
            }
            targets.forEach((t) => {
              if (t.value === null) return;
              const targetY = yScale.getPixelForValue(t.value);
              c.save();
              c.beginPath();
              c.setLineDash([2, 2]);
              const cpX = (lastPoint.x + rightX) / 2;
              const cpY = (lastPoint.y + targetY) / 2 + 15;
              c.moveTo(lastPoint.x, lastPoint.y);
              c.quadraticCurveTo(cpX, cpY, rightX, targetY);
              c.strokeStyle = t.hex;
              c.lineWidth = 1.5;
              c.globalAlpha = 0.7;
              c.stroke();
              c.restore();
              c.save();
              c.beginPath();
              c.arc(rightX, targetY, 4, 0, Math.PI * 2);
              c.fillStyle = t.hex;
              c.fill();
              c.closePath();
              c.restore();
              c.save();
              c.font = `700 ${this.compactFonts ? 10 : 12}px ${this.labelFont}, sans-serif`;
              c.fillStyle = this.targetLabelColor ?? (isLight ? '#000' : '#D9DDE0');
              c.textBaseline = 'middle';
              c.fillText(formatDisplayValue(t.value), rightX + 10, targetY);
              c.restore();
            });
          },
        },
        {
          id: 'endPoint',
          afterDraw: (chart) => {
            const meta = chart.getDatasetMeta(0);
            const last = meta.data[meta.data.length - 1];
            if (last) {
              const c = chart.ctx;
              c.beginPath();
              c.arc(last.x, last.y, 4, 0, Math.PI * 2);
              c.fillStyle = this.primaryColor;
              c.fill();
              c.closePath();
            }
          },
        },
      ],
    });
  }
}
